/* eslint-disable react/jsx-props-no-spreading */
import { ReactNode, forwardRef, ElementType } from 'react'
import { Typography, Toolbar, TableCell, Select, InputBase, MenuItem, Box } from '@mui/material'
import { unstable_useId as useId } from '@mui/material/utils'
import withStyles from '@mui/styles/withStyles'
import clsx from 'clsx'
import { t } from 'i18next'

import { AngleDownIcon2 } from '../ui/Icons'
import TablePaginationActions from './TablePaginationActions'

const defaultLabelDisplayedRows = ({ from, to, count }) => {
  return (
    <>
      <span>{`${from}-${to} `}</span>
      <span>{`${t('components.enhanced_table.footer.from')} ${
        count !== -1 ? count : `more than ${to}`
      }`}</span>
    </>
  )
}
const defaultRowsPerPageOptions = [5, 10, 25, 50]

type Props = {
  ActionsComponent?: ElementType
  backIconButtonProps?: Record<string, any>
  backIconButtonText?: string
  classes?: Record<string, any>
  className?: string
  colSpan?: number
  component?: ElementType
  count: number
  showLabelDisplayedRows?: boolean
  labelDisplayedRows?: ({
    from,
    to,
    count,
    page
  }: {
    from: number
    to: number
    count: number
    page: number
  }) => string
  labelRowsPerPage: ReactNode
  nextIconButtonProps?: Record<string, any>
  nextIconButtonText?: string
  onChangePage: (event, page) => void
  onChangeRowsPerPage: (event) => void
  page: number
  rowsPerPage: number
  rowsPerPageOptions: (number | { value: number; label: string })[]
  SelectProps: Record<string, any>
}

/**
 * A `TableCell` based component for placing inside `TableFooter` for pagination.
 */
const TablePagination = forwardRef(function(props: Props, ref) {
  const {
    ActionsComponent = TablePaginationActions,
    backIconButtonProps,
    backIconButtonText = 'Previous page',
    classes = {},
    className,
    colSpan: colSpanProp,
    component: Component = TableCell,
    count,
    labelDisplayedRows = defaultLabelDisplayedRows,
    labelRowsPerPage = 'Rows per page:',
    nextIconButtonProps,
    nextIconButtonText = 'Next page',
    onChangePage,
    onChangeRowsPerPage,
    page,
    rowsPerPage,
    rowsPerPageOptions = defaultRowsPerPageOptions,
    SelectProps = {},
    ...other
  } = props
  let colSpan

  if (Component === TableCell || Component === 'td') {
    colSpan = colSpanProp || 1000
  }

  const selectId = useId()
  const labelId = useId()
  const MenuItemComponent = SelectProps.native ? 'option' : MenuItem

  const from = count === 0 ? 0 : page * rowsPerPage + 1
  const to = count !== -1 ? Math.min(count, (page + 1) * rowsPerPage) : (page + 1) * rowsPerPage
  // const lastPage = Math.ceil(count / rowsPerPage)

  return (
    <Component className={clsx(classes.root, className)} colSpan={colSpan} ref={ref} {...other}>
      <Toolbar className={classes.toolbar}>
        <Box flex="1 1 auto" />
        <ActionsComponent
          className={classes.actions}
          backIconButtonProps={{
            title: backIconButtonText,
            'aria-label': backIconButtonText,
            ...backIconButtonProps
          }}
          count={count === 0 ? 1 : count}
          nextIconButtonProps={{
            title: nextIconButtonText,
            'aria-label': nextIconButtonText,
            ...nextIconButtonProps
          }}
          onChangePage={onChangePage}
          page={page}
          rowsPerPage={rowsPerPage}
        />
        <Box flex="1 1 auto" />
        {rowsPerPageOptions.length > 1 && (
          <Box display="flex" flexDirection="row" flex="0 0 auto" alignItems="center">
            <Typography color="secunday" variant="body2" className={classes.caption} id={labelId}>
              {labelRowsPerPage}
            </Typography>
            <Select
              classes={{
                select: classes.select,
                icon: classes.selectIcon
              }}
              input={<InputBase className={clsx(classes.input, classes.selectRoot)} />}
              value={rowsPerPage}
              onChange={onChangeRowsPerPage}
              id={selectId}
              labelId={labelId}
              {...SelectProps}
              IconComponent={_props => (
                <Box className="wrapperIcon">
                  <AngleDownIcon2 className={_props.className.toString()} sx={{ fontSize: 14 }} />
                </Box>
              )}
            >
              {rowsPerPageOptions.map(rowsPerPageOption => (
                <MenuItemComponent
                  className={classes.menuItem}
                  key={
                    typeof rowsPerPageOption === 'number'
                      ? rowsPerPageOption
                      : rowsPerPageOption.label
                  }
                  value={
                    typeof rowsPerPageOption === 'number'
                      ? rowsPerPageOption
                      : rowsPerPageOption.value
                  }
                >
                  {typeof rowsPerPageOption === 'number'
                    ? rowsPerPageOption
                    : rowsPerPageOption.label}
                </MenuItemComponent>
              ))}
            </Select>
            <Typography color="inherit" variant="body2" className={classes.totals}>
              {labelDisplayedRows({
                from,
                to,
                count: count === -1 ? -1 : count,
                page
              })}
            </Typography>
          </Box>
        )}
      </Toolbar>
    </Component>
  )
})

export default withStyles(
  theme => ({
    /* Styles applied to the root element. */
    root: {
      color: theme.palette.text.primary,
      fontSize: theme.typography.pxToRem(14),
      overflow: 'auto',
      borderTopWidth: 1,
      borderTopColor: theme.palette.primary.main,
      borderTopStyle: 'solid',
      // Increase the specificity to override TableCell.
      '&:last-child': {
        padding: 0
      }
    },
    /* Styles applied to the Toolbar component. */
    toolbar: {
      display: 'flex',
      justifyContent: 'center',
      padding: theme.spacing(1.5, 0, 0, 0),
      minHeight: 42,
      boxSizing: 'content-box'
    },
    /* Styles applied to the spacer element. */
    spacer: {
      flex: '1 1 100%'
    },

    caption: {
      color: theme.colors.gray14,
      fontFamily: theme.corporate.primaryFont,
      fontSize: 14,
      fontWeight: 400,
      lineHeight: '18px'
    },

    totals: {
      flex: '0 0  auto',
      padding: theme.spacing(0, 1.5),
      color: theme.colors.gray14,
      fontFamily: theme.corporate.primaryFont,
      fontSize: 14,
      fontWeight: 400,
      lineHeight: '18px',

      '& span:first-child': {
        fontWeight: 700
      }
    },
    // TODO v5: `.selectRoot` should be merged with `.input`
    /* Styles applied to the Select component root element. */
    selectRoot: {
      marginLeft: theme.spacing(0.5),
      marginRight: theme.spacing(1)
    },
    /* Styles applied to the Select component `select` class. */
    select: {
      width: '20px !important',
      height: '24px !important',
      paddingTop: '2px !important',
      paddingBottom: '2px !important',
      lineHeight: '24px !important',
      paddingLeft: 8,
      paddingRight: '26px !important',
      textAlign: 'center',
      textAlignLast: 'center', // Align <select> on Chrome.
      borderRadius: '4px',
      border: `1px solid ${theme.colors.gray17}`,
      backgroundColor: 'white'
    },
    // TODO v5: remove
    /* Styles applied to the Select component `icon` class. */
    selectIcon: {
      color: theme.palette.primary.main
    },
    /* Styles applied to the `InputBase` component. */
    input: {
      background: 'none',
      flexShrink: 0,
      padding: 0,
      color: theme.colors.gray14,
      fontFamily: theme.corporate.primaryFont,
      fontSize: 14,
      fontWeight: 400,
      lineHeight: '18px'
    },
    /* Styles applied to the MenuItem component. */
    menuItem: {
      color: theme.colors.gray14,
      fontFamily: theme.corporate.secundaryFont,
      fontSize: 14,
      fontWeight: 400,
      lineHeight: '18px',
      textAlign: 'center',

      '&[aria-selected="true"]': {
        color: theme.palette.primary.main
      }
    },
    /* Styles applied to the internal `TablePaginationActions` component. */
    actions: {
      flexShrink: 0,
      marginRight: theme.spacing(2)
    }
  }),
  { name: 'MuiTablePagination' }
)(TablePagination)
